import { Clip, ClipAnimation, ClipTransform, TimeMs } from '../types';
import { AnimationType } from '../../animations/types';

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

export type Viewport = {
  width: number;
  height: number;
};

export const computeClipTransformAt = (
  clip: Clip,
  t: TimeMs,
  viewport: Viewport,
  canonical: ClipTransform
): ClipTransform => {
  if (!clip.duration) return canonical;

  const local = t - clip.start;
  if (local < 0 || local >= clip.duration) return canonical;

  let transform = canonical;

  if (clip.enter && local < clip.enter.duration) {
    const progress = easeOutCubic(local / clip.enter.duration);
    transform = interpolate(clip.enter, transform, progress, 'in', viewport);
  }

  if (clip.exit) {
    const exitStart = clip.duration - clip.exit.duration;
    if (local >= exitStart) {
      const progress = easeOutCubic((local - exitStart) / clip.exit.duration);
      transform = interpolate(clip.exit, transform, progress, 'out', viewport);
    }
  }

  return transform;
};

const interpolate = (
  anim: ClipAnimation,
  base: ClipTransform,
  progress: number,
  direction: 'in' | 'out',
  viewport: Viewport
): ClipTransform => {
  const p = direction === 'in' ? 1 - progress : progress;
  return applyPreset(anim.preset, base, p, viewport);
};

const applyPreset = (preset: AnimationType, base: ClipTransform, p: number, viewport: Viewport): ClipTransform => {
  switch (preset) {
    case 'fadeIn':
    case 'fadeOut':
      return { ...base, opacity: base.opacity * (1 - p) };

    case 'slideLeft':
      return { ...base, left: base.left + viewport.width * p };

    case 'slideRight':
      return { ...base, left: base.left - viewport.width * p };

    case 'slideUp':
      return { ...base, top: base.top + viewport.height * p };

    case 'slideDown':
      return { ...base, top: base.top - viewport.height * p };

    case 'zoomIn':
    case 'zoomOut':
      return { ...base, scaleX: base.scaleX * (1 - p), scaleY: base.scaleY * (1 - p) };

    default:
      return base;
  }
};
